/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Coarse run state of the project's risk analysis coordinator. never: no run is visible, either because the project has never been analyzed or because its last run is older than Temporal's retention window. idle: the latest run has closed and the coordinator is waiting for the next chat write to wake it. running: a run is in flight right now.
 */
export const RiskAnalysisStatusResultState = {
  Never: "never",
  Idle: "idle",
  Running: "running",
} as const;
/**
 * Coarse run state of the project's risk analysis coordinator. never: no run is visible, either because the project has never been analyzed or because its last run is older than Temporal's retention window. idle: the latest run has closed and the coordinator is waiting for the next chat write to wake it. running: a run is in flight right now.
 */
export type RiskAnalysisStatusResultState = ClosedEnum<
  typeof RiskAnalysisStatusResultState
>;

export type RiskAnalysisStatusResult = {
  /**
   * When the most recent closed run finished; the moment the Watchdog findings were last brought up to date. Set only when state is idle.
   */
  lastRunAt?: Date | undefined;
  /**
   * How the most recent closed run ended: completed, failed, canceled, terminated, continued_as_new, timed_out, or unknown. continued_as_new is the normal outcome for a long-lived coordinator that rolled its history over, so treat it like completed. Set only when state is idle.
   */
  lastRunOutcome?: string | undefined;
  /**
   * When the most recent closed run started. Set only when state is idle.
   */
  lastRunStartedAt?: Date | undefined;
  /**
   * When the in-flight run started. Set only when state is running.
   */
  runningSince?: Date | undefined;
  /**
   * Coarse run state of the project's risk analysis coordinator. never: no run is visible, either because the project has never been analyzed or because its last run is older than Temporal's retention window. idle: the latest run has closed and the coordinator is waiting for the next chat write to wake it. running: a run is in flight right now.
   */
  state: RiskAnalysisStatusResultState;
};

/** @internal */
export const RiskAnalysisStatusResultState$inboundSchema: z.ZodMiniEnum<
  typeof RiskAnalysisStatusResultState
> = z.enum(RiskAnalysisStatusResultState);

/** @internal */
export const RiskAnalysisStatusResult$inboundSchema: z.ZodMiniType<
  RiskAnalysisStatusResult,
  unknown
> = z.pipe(
  z.object({
    last_run_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    last_run_outcome: z.optional(z.string()),
    last_run_started_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    running_since: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    state: RiskAnalysisStatusResultState$inboundSchema,
  }),
  z.transform((v) => {
    return remap$(v, {
      "last_run_at": "lastRunAt",
      "last_run_outcome": "lastRunOutcome",
      "last_run_started_at": "lastRunStartedAt",
      "running_since": "runningSince",
    });
  }),
);

export function riskAnalysisStatusResultFromJSON(
  jsonString: string,
): SafeParseResult<RiskAnalysisStatusResult, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => RiskAnalysisStatusResult$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'RiskAnalysisStatusResult' from JSON`,
  );
}
